import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import RoleBasedNavigation from '../../components/navigation/RoleBasedNavigation';
import BreadcrumbTrail from '../../components/navigation/BreadcrumbTrail';
import Icon from '../../components/AppIcon';
import InviteCodeToggle from './components/InviteCodeToggle';
import LogoUploadSection from './components/LogoUploadSection';
import HeroImageManager from './components/HeroImageManager';
import FounderBioEditor from './components/FounderBioEditor';
import PlaysEditor from './components/PlaysEditor';
import ConfigurationAudit from './components/ConfigurationAudit';
import SystemStatusCard from './components/SystemStatusCard';
import SecuritySettings from './components/SecuritySettings';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { systemMonitoringService } from '../../services/systemMonitoringService';

const SiteSettings = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [userProfile, setUserProfile] = useState(null);

  // Mock superadmin user
  const mockUser = {
    id: 1,
    name: "Mike Johnson",
    email: "mike@poetsroad.com",
    role: "superadmin",
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_19b00e0ce-1763292994800.png",
    avatarAlt: "Professional headshot of middle-aged man with gray hair wearing navy blazer"
  };

  // Mock notifications
  const mockNotifications = [
  {
    id: 1,
    type: "info",
    title: "System Update",
    message: "Platform maintenance scheduled for tonight at 2 AM EST",
    timestamp: new Date(Date.now() - 3600000),
    read: false
  }];


  // Settings state
  const [inviteCodeEnabled, setInviteCodeEnabled] = useState(true);
  const [inviteCodeLastModified, setInviteCodeLastModified] = useState(new Date());
  const [horizontalLogo, setHorizontalLogo] = useState(null);
  const [verticalLogo, setVerticalLogo] = useState(null);

  // Audit log state
  const [auditLog, setAuditLog] = useState([]);

  // System status state
  const [systemStatus] = useState({
    status: "operational",
    activeUsers: 1247,
    databaseSize: "2.4 GB",
    storageUsed: "1.8 GB",
    uptime: "99.9%",
    lastUpdated: new Date()
  });

  useEffect(() => {
    const checkAccess = async () => {
      if (!isAuthenticated || !user) {
        navigate('/login');
        return;
      }

      try {
        // Fetch user profile to check role
        const { data: profile, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', user?.id)?.single();

        if (error) throw error;

        if (profile?.role !== 'superadmin') {
          navigate('/admin-dashboard');
          return;
        }

        setUserProfile(profile);

        // Fetch site settings
        const { data: settings, error: settingsError } = await supabase?.from('site_settings')?.select('*')?.single();

        if (!settingsError && settings) {
          setHorizontalLogo(settings?.horizontal_logo_url);
          setVerticalLogo(settings?.vertical_logo_url);
        }

        setIsLoading(false);
      } catch (error) {
        console.error('Access check error:', error);
        navigate('/admin-dashboard');
      }
    };

    checkAccess();
  }, [isAuthenticated, user, navigate]);

  const handleLogoUpload = async (type, logoUrl) => {
    if (type === 'horizontal') {
      setHorizontalLogo(logoUrl);
    } else {
      setVerticalLogo(logoUrl);
    }

    // Create audit entry in Supabase
    try {
      await systemMonitoringService?.createAuditEntry(
        'logo_updated',
        `${type?.charAt(0)?.toUpperCase() + type?.slice(1)} logo updated and optimized`,
        { type, url: logoUrl }
      );
    } catch (error) {
      console.error('Error creating audit entry:', error);
    }
  };

  const handleBackupCreate = async () => {
    try {
      await systemMonitoringService?.createAuditEntry(
        'backup_created',
        'Configuration backup created successfully',
        { timestamp: new Date()?.toISOString() }
      );
    } catch (error) {
      console.error('Error creating audit entry:', error);
    }
  };

  const handlePasswordChange = async (passwordData) => {
    try {
      await systemMonitoringService?.createAuditEntry(
        'password_changed',
        'Superadmin password updated',
        { timestamp: new Date()?.toISOString() }
      );
    } catch (error) {
      console.error('Error creating audit entry:', error);
    }
  };

  const breadcrumbs = [
  { label: 'Dashboard', path: '/admin-dashboard' },
  { label: 'Site Settings', path: '/site-settings' }];


  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent mb-4"></div>
          <p className="text-muted-foreground">Loading settings...</p>
        </div>
      </div>);

  }

  return (
    <div className="min-h-screen bg-background">
      <RoleBasedNavigation
        user={mockUser}
        isAuthenticated={true}
        notifications={mockNotifications} />

      <main className="main-content">
        <BreadcrumbTrail customBreadcrumbs={breadcrumbs} />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Icon name="Settings" size={24} className="text-primary" />
              </div>
              <h1 className="text-3xl font-bold text-foreground">Site Settings</h1>
            </div>
            <p className="text-muted-foreground">
              Manage platform configuration, branding, and system settings
            </p>
          </div>

          {/* Access Control Notice */}
          <div className="bg-accent/10 border border-accent/20 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <Icon name="Shield" size={20} className="text-accent flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-foreground mb-1">
                  Superadmin Access Only
                </p>
                <p className="text-sm text-muted-foreground">
                  You are logged in as {mockUser?.email}. These settings control platform-wide configuration and are restricted to superadmin access only.
                </p>
              </div>
            </div>
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Primary Settings */}
            <div className="lg:col-span-2 space-y-6">
              <InviteCodeToggle />

              <LogoUploadSection
                horizontalLogo={horizontalLogo}
                verticalLogo={verticalLogo}
                onLogoUpload={handleLogoUpload} />

              <HeroImageManager />

              <FounderBioEditor userId={user?.id} />

              <PlaysEditor userId={user?.id} />

              <SecuritySettings
                onBackupCreate={handleBackupCreate}
                onPasswordChange={handlePasswordChange} />
            </div>

            {/* Right Column - Status & Audit */}
            <div className="space-y-6">
              <SystemStatusCard />
              <ConfigurationAudit />
            </div>
          </div>

          {/* Footer Notice */}
          <div className="mt-8 bg-muted/30 border border-border rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Icon name="Info" size={20} className="text-muted-foreground flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-muted-foreground">
                  All configuration changes are automatically saved and logged for security purposes. Changes take effect immediately across the platform. For critical system modifications, consider creating a backup first.
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>);

};

export default SiteSettings;